import { inject, NgZone } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  Conversation,
  ConversationChannel,
  GlobalParticipantType,
  Participant,
  PlatformLocation,
  MetadataIdentifier,
} from '@vendasta/conversation';
import { FIRESTORE_CONVERSATION_LIMIT, POSTHOG_CHANNEL_NAME } from './conversation.constants';
import { PlatformLocationToSKey } from './inbox-terms-of-service.service';
import { FOLLOWING_VIEW_ID } from './inbox.constants';
import { ConversationDetail } from './interface/conversation.interface';
import { CONVERSATION_ROUTES_TOKEN } from './tokens';

/**
 * conversation was not seen if the participant isn't in the `lastSeenTimeByParticipantField` or `lastSeenTime` of the participant is less than `latestMsgSentTime`
 * @param {FirestoreConversation} conversation - the conversation
 */
export function conversationUnseen(conversation: Conversation, participantId: string): boolean {
  if (!conversation?.latestRelevantActivityTime) {
    return false;
  }

  if (!participantId) {
    return true;
  } else {
    const lastSeenTimeByParticipant = conversation?.lastSeenByParticipant?.find(
      (lastSeenByParticipant) => lastSeenByParticipant?.participantId === participantId,
    );
    return (
      !lastSeenTimeByParticipant || lastSeenTimeByParticipant?.lastSeenTime < conversation?.latestRelevantActivityTime
    );
  }
}

export function getConversationByContactID(
  contactID: string,
  conversations: ConversationDetail[],
): ConversationDetail | null {
  let contactHasConversation: ConversationDetail | null = null;

  conversationsLoop: for (let i = 0; i < conversations.length; i++) {
    const conversation = conversations[i];
    for (let j = 0; j < conversation.participants.length; j++) {
      const participant = conversation.participants[j];
      if (participant?.internalParticipantId === contactID) {
        contactHasConversation = conversation;
        break conversationsLoop;
      }
    }
  }

  return contactHasConversation;
}

export function getPlatformLocationToSKey(platformLocation: PlatformLocation): PlatformLocationToSKey | undefined {
  switch (platformLocation) {
    case PlatformLocation.PLATFORM_LOCATION_VENDOR_CENTER:
    case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
    case PlatformLocation.PLATFORM_LOCATION_TASK_MANAGER:
    case PlatformLocation.PLATFORM_LOCATION_SALES_CENTER:
      return { partnerId: 'ALL', feature: 'inbox-platform' };
    case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
      return { partnerId: 'ALL', feature: 'inbox-conversation' };
  }
}

/**
 * Concat two URLs to generate an unique URL with both strings
 * @param {string} entryUrl - entry Url
 * @param {string} nextUrl - next Url
 * @return {string} - composed URL
 */
export function addNextUrl(entryUrl: string, nextUrl: string): string {
  if (!('URL' in window)) {
    return entryUrl + `?nextUrl=${encodeURIComponent(nextUrl)}`;
  }
  const urlObj = new URL(entryUrl);
  const params = new URLSearchParams(urlObj.search);
  params.append('nextUrl', nextUrl);
  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * Create a following view id from a participant
 */
export function getFollowViewID(participantID: string): string {
  return participantID + ':' + FOLLOWING_VIEW_ID;
}

export function extractCurrentLocation(platformLocationsEnabled: PlatformLocation[]): PlatformLocation {
  const currentLocation = platformLocationsEnabled?.length > 0 ? platformLocationsEnabled[0] : null;
  if (!currentLocation) {
    throw new Error(
      'You need to set up the platformLocationsEnabled in the ConversationConfig. To fix see documentation in the README of @galaxy/conversation/core',
    );
  }
  return currentLocation;
}

/**
 * Format the number of unread conversations by view to display
 */
export function formatUnreadConversationsCount(count: number, limit = FIRESTORE_CONVERSATION_LIMIT): string {
  if (!count || count === 0) {
    return '';
  }
  return count < limit ? count.toString() : `${limit}+`;
}

/** Shape of an event this key reads; the generated Event satisfies it structurally. */
interface DatedEventLike {
  eventId?: string;
  happenedAt?: Date;
}

/**
 * Identity of the newest event in a list, stable across reorderings.
 *
 * The array arrives in `getMultiEvents` response order, so index 0 is not dependably the newest;
 * event id breaks a tie on `happenedAt` so the key does not flip between loads.
 */
export function newestEventKey(events: DatedEventLike[] | undefined | null): string {
  const newest = (events ?? []).reduce<{ at: number; eventId: string } | undefined>((best, event) => {
    const candidate = { at: event.happenedAt?.getTime() ?? 0, eventId: event.eventId ?? '' };
    return !best || candidate.at > best.at || (candidate.at === best.at && candidate.eventId > best.eventId)
      ? candidate
      : best;
  }, undefined);

  return newest ? `${newest.at}:${newest.eventId}` : '';
}

const ID_PREFIX = 'CONVERSATION-';

export function toFirestoreId(id: string): string {
  return id.replace(ID_PREFIX, '');
}

export function fromFirestoreId(id: string): string {
  if (id && id.startsWith(ID_PREFIX)) {
    return id;
  }
  return ID_PREFIX + id;
}

export const redirectToConversationFactory = () => {
  const zone = inject(NgZone);
  const router = inject(Router);
  const routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN), { requireSync: true });

  return (conversationId: string) => {
    zone.run(() => {
      router.navigate([routes().root + '/inbox/channel', toFirestoreId(conversationId)], {
        queryParamsHandling: 'merge',
      });
    });
  };
};

/**
 * Build the properties for the send message track event in Posthog
 */
export function buildSendMessageTrackProperties(
  conversationId: string,
  sender: Participant,
  channel: ConversationChannel,
  numberOfFiles: number,
  platformLocation: PlatformLocation,
) {
  return {
    id: conversationId,
    accountGroupId: sender.accountGroupId,
    participantName: sender?.name || '',
    partnerId: sender.partnerId,
    PID_AGID: getPosthogNamespace(sender.partnerId, sender.accountGroupId),
    location: platformLocation,
    channel: getPosthogChannelName(channel),
    type: sender?.participantType,
    numberOfFiles: numberOfFiles,
  };
}

export function getPosthogChannelName(channel: ConversationChannel): string {
  return POSTHOG_CHANNEL_NAME[channel];
}

export function getPosthogNamespace(partnerId: string, accountGroupId: string): string {
  return accountGroupId ? partnerId + '_' + accountGroupId : partnerId;
}

export function isAccountGroupAndCustomerConversation(conversation?: Conversation): boolean {
  // ideally we should be using subjectParticipantKey for this check.
  return (
    !!conversation &&
    conversation.subjectParticipantKey?.subjectParticipants?.some(
      (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
    ) &&
    conversation.subjectParticipantKey?.subjectParticipants?.some(
      (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER,
    )
  );
}

export function getConversationIdFromDetail(detail?: ConversationDetail): string {
  return detail?.conversation?.conversationId || '';
}

export function getAccountGroupIDFromConversation(conversation?: Conversation): string {
  return (
    conversation?.subjectParticipantKey.subjectParticipants.find(
      (subjectParticipant) =>
        subjectParticipant?.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
    )?.internalParticipantId || ''
  );
}

/**
 * Builds metadata with the current page URL for AI context
 * @returns Metadata array with sender information including current page URL
 */
export function buildAIContextMetadata() {
  return [
    {
      identifier: MetadataIdentifier.METADATA_IDENTIFIER_SENDER,
      data: {
        senderOriginURL: window.location.href,
        senderTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
  ];
}

/** Strip markup from a preview string, collapsing whitespace. */
export function htmlToPlainText(value: string | undefined | null): string {
  if (!value) {
    return '';
  }

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  parsed.body.querySelectorAll('script,style,template').forEach((element) => element.remove());
  return (parsed.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Elements a preview may render. Everything else is unwrapped to its text, and every attribute goes. */
const EMAIL_PREVIEW_ELEMENTS = new Set([
  'P',
  'DIV',
  'SPAN',
  'BR',
  'HR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'SMALL',
  'SUB',
  'SUP',
  'UL',
  'OL',
  'LI',
  'BLOCKQUOTE',
  'PRE',
  'CODE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TD',
  'TH',
]);

/** Dropped with their contents, rather than unwrapped, so their fallback text is not shown either. */
const EMAIL_PREVIEW_DROPPED = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'IMG',
  'PICTURE',
  'VIDEO',
  'AUDIO',
  'SOURCE',
  'TRACK',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'SVG',
  'CANVAS',
  'MAP',
  'FORM',
  'BUTTON',
  'SELECT',
  'TEXTAREA',
]);

/**
 * An inbound email's markup, reduced to what a preview may safely render.
 *
 * An allowlist, because nothing about a preview may fetch: a remote image, audio preload, a table
 * cell's `background` or an inline `background-image` all load as the inbox paints, confirming the
 * read to the sender before anyone opens the message. A denylist has to know every such element
 * and attribute; this only has to know the handful a preview needs, and strips every attribute from
 * those. The dropped set is not what keeps it safe, since an unlisted element is unwrapped anyway;
 * it only stops media from leaving its fallback text behind.
 *
 * Anchors are unwrapped with the rest. The whole card is already a click target that opens the
 * thread, so a live link inside it either navigates the app away from the inbox or fights the
 * card's own handler.
 *
 * Paragraphs with no text go too. A mail client's spacer paragraphs would otherwise spend the few
 * lines a card shows on nothing, and in markup they are decidable without guessing at the client.
 */
export function emailPreviewMarkup(value: string | undefined | null): string {
  if (!value) {
    return '';
  }

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  for (const element of Array.from(parsed.body.querySelectorAll('*'))) {
    if (!element.isConnected) {
      continue;
    }
    if (EMAIL_PREVIEW_DROPPED.has(element.tagName)) {
      element.remove();
    } else if (!EMAIL_PREVIEW_ELEMENTS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
    } else {
      element.getAttributeNames().forEach((name) => element.removeAttribute(name));
    }
  }

  Array.from(parsed.body.querySelectorAll('p, div'))
    .reverse()
    .filter((block) => !block.textContent?.trim())
    .forEach((block) => block.remove());

  return parsed.body.innerHTML;
}

const ELLIPSIS = '\u2026';

/**
 * Truncate to a code point ceiling, so a multi-byte character is never split at the boundary.
 * A truncated string ends in an ellipsis, which counts against the ceiling rather than exceeding it.
 */
export function truncateToCodePoints(value: string, maxCodePoints: number): string {
  const text = value ?? '';
  if (text.length <= maxCodePoints) {
    return text;
  }

  const codePoints = Array.from(text);
  if (codePoints.length <= maxCodePoints) {
    return text;
  }
  return (
    codePoints
      .slice(0, Math.max(0, maxCodePoints - 1))
      .join('')
      .trimEnd() + ELLIPSIS
  );
}
